import React, { useState, useEffect } from 'react';
import { doc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { 
  RulerDimensionLine, Plus, Search, Edit, Trash2, Hash, GripVertical, 
  AlertCircle, Info, ChevronUp, ChevronDown, X 
} from 'lucide-react';
import SpecForm from './SpecForm';
import SearchCounter from '../SearchCounter';
import { useAuth } from '../../contexts/AuthContext';
import { getAllSpecs, checkSpecInUse } from '../../lib/specService';
import { matchesSearchTerm } from '../../lib/searchUtils';

const SpecificationsTab = () => {
  const { userRole } = useAuth();
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSpec, setEditingSpec] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSpecs();
  }, []);

  const fetchSpecs = async () => {
    try {
      setLoading(true);
      
      // Use local-first spec service
      const specsList = await getAllSpecs();
      setSpecs(specsList);
    } catch (error) {
      console.error('Error fetching specs:', error);
      console.log('Specs collection may not exist yet, starting with empty list');
      setSpecs([]);
    } finally {
      setLoading(false);
    }
  };


  const filteredSpecs = specs.filter(spec => {
    // If no search term, include all specs
    if (!searchTerm || searchTerm.trim() === '') {
      return true;
    }
    
    return (
      (spec.nombre && matchesSearchTerm(spec.nombre, searchTerm)) ||
      (spec.descripcion && matchesSearchTerm(spec.descripcion, searchTerm)) ||
      (spec.unidad && matchesSearchTerm(spec.unidad, searchTerm))
    );
  });

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const canPerformActions = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const handleFormSuccess = async () => {
    const message = editingSpec ? 'Especificación actualizada exitosamente' : 'Especificación creada exitosamente';
    setSuccess(message);
    setEditingSpec(null);
    await fetchSpecs(); // Refresh specs with updated counts
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleEdit = (spec) => {
    setEditingSpec(spec);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSpec(null);
  };


  const handleDelete = async (spec) => {
    if (!canDelete()) {
      setError('No tienes permisos para eliminar especificaciones');
      setTimeout(() => setError(''), 5000);
      return;
    }

    try {
      // Check if spec is used in types or equipment
      const isInUse = await checkSpecInUse(spec.nombre);
      
      if (isInUse) {
        setError(`No se puede eliminar la especificación "${spec.nombre}" porque está siendo usada en tipos de equipo o equipos del catálogo.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      // Confirm deletion
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente la especificación "${spec.nombre}"?\n\n` +
        `Esta acción no se puede deshacer.`
      )) {
        return;
      }

      // Delete the spec
      await deleteDoc(doc(db, 'specs', spec.id));
      setSuccess(`Especificación "${spec.nombre}" eliminada exitosamente`);
      fetchSpecs();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting spec:', error);
      setError('Error al eliminar la especificación');
      setTimeout(() => setError(''), 5000);
    }
  };


  // Drag and drop functionality for reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    if (!canEdit()) {
      setError('No tienes permisos para reordenar especificaciones');
      setTimeout(() => setError(''), 5000);
      setDraggedIndex(null);
      return;
    }

    try {
      setReorderLoading(true);
      
      // Create a copy of the filtered specs array for manipulation
      const newFilteredSpecs = [...filteredSpecs];
      const draggedSpec = newFilteredSpecs[draggedIndex];
      
      // Remove the dragged item and insert it at the new position
      newFilteredSpecs.splice(draggedIndex, 1);
      newFilteredSpecs.splice(dropIndex, 0, draggedSpec);
      
      // Update the local state immediately to reflect the change
      setSpecs(currentSpecs => {
        const newSpecs = [...currentSpecs];
        // Find and reorder only the specs that are currently visible (filtered)
        const filteredIds = new Set(newFilteredSpecs.map(spec => spec.id));
        
        // Update priorities only for filtered specs
        const batch = writeBatch(db);
        newFilteredSpecs.forEach((spec, index) => {
          const specRef = doc(db, 'specs', spec.id);
          batch.update(specRef, { prioridad: index + 1 });
          
          // Update the local state as well
          const specIndex = newSpecs.findIndex(s => s.id === spec.id);
          if (specIndex !== -1) {
            newSpecs[specIndex] = { ...newSpecs[specIndex], prioridad: index + 1 };
          }
        });
        
        // Commit the batch update
        batch.commit().catch(error => {
          console.error('Error committing batch update:', error);
          setError('Error al guardar el nuevo orden');
          setTimeout(() => setError(''), 5000);
        });
        
        return newSpecs.sort((a, b) => a.prioridad - b.prioridad);
      });
      
      setSuccess('Orden de especificaciones actualizado exitosamente');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error reordering specs:', error);
      setError('Error al reordenar especificaciones');
      setTimeout(() => setError(''), 5000);
    } finally {
      setReorderLoading(false);
      setDraggedIndex(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const moveSpecUp = async (spec, currentIndex) => {
    if (currentIndex === 0 || !canEdit()) return;
    
    try {
      setReorderLoading(true);
      const specAbove = filteredSpecs[currentIndex - 1];
      
      // Create new array with swapped positions
      const newFilteredSpecs = [...filteredSpecs];
      newFilteredSpecs[currentIndex] = specAbove;
      newFilteredSpecs[currentIndex - 1] = spec;
      
      // Update local state immediately by reordering the entire list
      setSpecs(currentSpecs => {
        const newSpecs = [...currentSpecs];
        
        // Update priorities for the reordered filtered specs
        newFilteredSpecs.forEach((updatedSpec, index) => {
          const specIndex = newSpecs.findIndex(s => s.id === updatedSpec.id);
          if (specIndex !== -1) {
            newSpecs[specIndex] = { ...newSpecs[specIndex], prioridad: index + 1 };
          }
        });
        
        return newSpecs.sort((a, b) => a.prioridad - b.prioridad);
      });
      
      // Update database with swapped priorities
      await updateDoc(doc(db, 'specs', spec.id), { prioridad: specAbove.prioridad });
      await updateDoc(doc(db, 'specs', specAbove.id), { prioridad: spec.prioridad });
      
      setSuccess('Especificación movida hacia arriba');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error moving spec up:', error);
      setError('Error al mover especificación');
      setTimeout(() => setError(''), 5000);
      // Revert state on error
      await fetchSpecs();
    } finally {
      setReorderLoading(false);
    }
  };

  const moveSpecDown = async (spec, currentIndex) => {
    if (currentIndex === filteredSpecs.length - 1 || !canEdit()) return;
    
    try {
      setReorderLoading(true);
      const specBelow = filteredSpecs[currentIndex + 1];
      
      // Create new array with swapped positions
      const newFilteredSpecs = [...filteredSpecs];
      newFilteredSpecs[currentIndex] = specBelow;
      newFilteredSpecs[currentIndex + 1] = spec;
      
      // Update local state immediately by reordering the entire list
      setSpecs(currentSpecs => {
        const newSpecs = [...currentSpecs];
        
        // Update priorities for the reordered filtered specs
        newFilteredSpecs.forEach((updatedSpec, index) => {
          const specIndex = newSpecs.findIndex(s => s.id === updatedSpec.id);
          if (specIndex !== -1) {
            newSpecs[specIndex] = { ...newSpecs[specIndex], prioridad: index + 1 };
          }
        });
        
        return newSpecs.sort((a, b) => a.prioridad - b.prioridad);
      });
      
      // Update database with swapped priorities
      await updateDoc(doc(db, 'specs', spec.id), { prioridad: specBelow.prioridad });
      await updateDoc(doc(db, 'specs', specBelow.id), { prioridad: spec.prioridad });
      
      setSuccess('Especificación movida hacia abajo');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error moving spec down:', error);
      setError('Error al mover especificación');
      setTimeout(() => setError(''), 5000);
      // Revert state on error
      await fetchSpecs();
    } finally {
      setReorderLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <RulerDimensionLine className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Especificaciones</h2>
        </div>
        {canEdit() && (
          <button 
            onClick={() => setShowForm(true)}
            className="flex items-center justify-center w-10 h-10 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400"
            title="Nueva Especificación"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Bar and Info */}
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {/* Reordering info */}
        {filteredSpecs.length > 1 && canEdit() && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Reordenar Especificaciones</p>
                <p>
                  Puedes cambiar el orden de prioridad arrastrando las filas o usando los botones de flecha. 
                  El orden determina cómo aparecen las especificaciones en los formularios de equipos.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar especificaciones por nombre, descripción o unidad..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                title="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <SearchCounter 
            count={filteredSpecs.length} 
            label="especificaciones" 
            isSearching={!!searchTerm} 
          />
        </div>
      </div>

      {/* Specs List */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando especificaciones...
          </div>
        ) : filteredSpecs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron especificaciones que coincidan con la búsqueda' : 'No hay especificaciones registradas'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {canEdit() && (
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16 hidden sm:table-cell">
                      Orden
                    </th>
                  )}
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Prioridad
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Especificación
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Uso en Tipos
                  </th>
                  {canPerformActions() && (
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSpecs.map((spec, index) => (
                  <tr 
                    key={spec.id} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${draggedIndex === index ? 'opacity-50' : ''} ${reorderLoading ? 'opacity-50' : ''}`}
                    draggable={canEdit()}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    {canEdit() && (
                      <td className="px-3 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="flex flex-col items-center space-y-1">
                          {/* Drag handle */}
                          <div className="cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                            <GripVertical className="h-4 w-4" />
                          </div>
                          
                          {/* Arrow buttons */}
                          <div className="flex flex-col space-y-1">
                            <button
                              onClick={() => moveSpecUp(spec, index)}
                              disabled={index === 0 || reorderLoading}
                              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Mover hacia arriba"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => moveSpecDown(spec, index)}
                              disabled={index === filteredSpecs.length - 1 || reorderLoading}
                              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Mover hacia abajo"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        #{spec.prioridad}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center">
                        <RulerDimensionLine className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {spec.nombre}
                          </div>
                          {spec.descripcion && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate" title={spec.descripcion}>
                              {spec.descripcion}
                            </div>
                          )}
                          {/* Mobile: Show priority below name when column is hidden */}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 md:hidden">
                            Prioridad: #{spec.prioridad}
                          </div>
                          {/* Mobile: Show usage count below when column is hidden */}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 lg:hidden">
                            {spec.typeUsageCount || 0} tipos de equipo
                          </div>
                          {/* Mobile: Show reorder controls when order column is hidden */}
                          {canEdit() && (
                            <div className="flex items-center space-x-2 mt-2 sm:hidden">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Orden:</span>
                              <button
                                onClick={() => moveSpecUp(spec, index)}
                                disabled={index === 0 || reorderLoading}
                                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed bg-gray-50 dark:bg-gray-700 rounded"
                                title="Mover hacia arriba"
                              >
                                <ChevronUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => moveSpecDown(spec, index)}
                                disabled={index === filteredSpecs.length - 1 || reorderLoading}
                                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed bg-gray-50 dark:bg-gray-700 rounded"
                                title="Mover hacia abajo"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </button>
                              <div className="cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 bg-gray-50 dark:bg-gray-700 rounded" title="Arrastra para reordenar">
                                <GripVertical className="h-3 w-3" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                        <Hash className="h-3 w-3 mr-1" />
                        {spec.typeUsageCount || 0} tipos
                      </span>
                    </td>
                    {canPerformActions() && (
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {canEdit() && (
                            <button
                              onClick={() => handleEdit(spec)}
                              className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title="Editar especificación"
                              disabled={reorderLoading}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete() && (
                            <button
                              onClick={() => handleDelete(spec)}
                              className={`p-2 rounded ${
                                (spec.typeUsageCount > 0 || reorderLoading)
                                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                  : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                              }`}
                              title={
                                spec.typeUsageCount > 0
                                  ? `No se puede eliminar - está siendo usada en ${spec.typeUsageCount} tipo(s) de equipo`
                                  : reorderLoading
                                  ? 'Reordenando especificaciones...'
                                  : 'Eliminar especificación'
                              }
                              disabled={spec.typeUsageCount > 0 || reorderLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Spec Form Modal */}
      <SpecForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editingSpec={editingSpec}
      />
    </div>
  );
};

export default SpecificationsTab;